import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TableToolbarRowProps {
  start?: ReactNode;
  center?: ReactNode;
  end?: ReactNode;
  className?: string;
}

export const TableToolbarRow = ({ start, center, end, className }: TableToolbarRowProps) => (
  <div className={cn('grid min-h-10 grid-cols-[1fr_auto_1fr] items-center gap-2 pl-1', className)}>
    <div className="justify-self-start">{start}</div>
    <div className="justify-self-center">{center}</div>
    <div className="flex items-center gap-2 justify-self-end">{end}</div>
  </div>
);

const SELECTION_GROUP_CLASSES =
  'flex h-9 items-center rounded-lg border border-border bg-background motion-reduce:animate-none';

type SelectionActionButtonProps = ComponentProps<typeof Button> & { label: string };

export const SelectionActionButton = ({ label, ...props }: SelectionActionButtonProps) => (
  <InfoTooltip content={label}>
    <Button variant="ghost-neutral" size="icon-sm" aria-label={label} {...props} />
  </InfoTooltip>
);

interface SelectionActionsProps {
  count: number;
  getLabel: (count: number) => string;
  onDeselectAll: () => void;
  children: ReactNode;
}

export const SelectionActions = ({
  count,
  getLabel,
  onDeselectAll,
  children,
}: SelectionActionsProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isOpen = count > 0;
  const [shownCount, setShownCount] = useState(count);
  if (isOpen && count !== shownCount) {
    setShownCount(count);
  }

  useEffect(() => {
    const element = ref.current;
    if (isOpen || !element) {
      return;
    }
    const animations = element.getAnimations?.({ subtree: true }) ?? [];
    if (animations.length === 0) {
      setShownCount(0);
      return;
    }
    let cancelled = false;
    Promise.all(animations.map((animation) => animation.finished)).then(
      () => !cancelled && setShownCount(0),
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (shownCount === 0) {
    return null;
  }

  return (
    <div ref={ref} className="flex items-center gap-2" inert={!isOpen}>
      <div
        className={cn(
          SELECTION_GROUP_CLASSES,
          'z-10 gap-0.5 pr-px pl-3.25',
          isOpen
            ? 'duration-150 animate-in fade-in'
            : 'delay-150 duration-100 animate-out fade-out fill-mode-both'
        )}
      >
        <span className="grid text-sm leading-5 font-medium whitespace-nowrap text-foreground tabular-nums">
          <span className="col-start-1 row-start-1">{getLabel(shownCount)}</span>
          <span aria-hidden className="invisible col-start-1 row-start-1">
            {getLabel(Math.max(shownCount, 10))}
          </span>
        </span>
        <SelectionActionButton label={t('common.deselect_all')} onClick={onDeselectAll}>
          <XIcon />
        </SelectionActionButton>
      </div>
      <div
        className={cn(
          SELECTION_GROUP_CLASSES,
          'gap-1.5 px-px',
          isOpen
            ? 'delay-300 duration-200 ease-out animate-in fade-in fill-mode-backwards slide-in-from-left-8'
            : 'duration-150 ease-in animate-out fade-out fill-mode-forwards slide-out-to-left-8'
        )}
      >
        {children}
      </div>
    </div>
  );
};
